<template>
  <button
    class="product-actions wc-card-hover"
    type="button"
    :id="'coupon_' + couponId"
    :class="{ clipped: isClippedOrRedeemed }"
    @click="handleClick"
    :aria-pressed="isClipped || isRedeemed ? 'true' : 'false'"
    :disabled="isDisabled || isClippedOrRedeemed"
  >
    <font-awesome-icon
      v-if="!isClipped && !isRedeemed"
      icon="scissors"
      class="product-add-icon"
      aria-hidden="true"
    />
    <font-awesome-icon v-else icon="check" class="product-add-icon" aria-hidden="true" />
    <span class="product-add-label">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed, defineProps, withDefaults } from 'vue';
import { COUPON_STATUS_FILTERS } from '@/constants/CouponConstants';

interface Props {
  couponId: string;
  availableLabel: string;
  clippedLabel: string;
  redeemedLabel: string;
  couponFilter?: string;
  isClipped?: boolean;
  isRedeemed?: boolean;
  disabled?: boolean;
  handleClip?: Function;
}

const props = withDefaults(defineProps<Props>(), {
  couponFilter: undefined,
  isClipped: false,
  isRedeemed: false,
  disabled: false,
  handleClip: () => {},
});

const isDisabled = computed(() => props.disabled);

const isClippedOrRedeemed = computed(
  () =>
    props.isClipped ||
    props.isRedeemed ||
    props.couponFilter === COUPON_STATUS_FILTERS.CLIPPED ||
    props.couponFilter === COUPON_STATUS_FILTERS.REDEEMED,
);

const label = computed({
  get: () => {
    if (props.couponFilter === COUPON_STATUS_FILTERS.CLIPPED) {
      return props.clippedLabel;
    }
    if (props.couponFilter === COUPON_STATUS_FILTERS.REDEEMED) {
      return props.redeemedLabel;
    }
    if (props.isClipped) {
      return props.clippedLabel;
    }
    if (props.isRedeemed) {
      return props.redeemedLabel;
    }
    return props.availableLabel;
  },
  set: newLabel => newLabel,
});

const handleClick = () => {
  if (!props.isClipped) {
    props.handleClip(props.couponId);
  }
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.product-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  padding: 0.625rem;
  background: var(--white, $white);
  border: none;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 0.5rem 0.5rem;
  font-size: $font-size-sm;
  font-weight: 700;
  color: var(--primary, $primary);
  cursor: pointer;
}

.product-actions.clipped,
.product-actions:disabled {
  cursor: default;
}

.product-actions:hover:not(:disabled) {
  background: var(--primary-lighter-9);
  border-top-color: transparent;
}

.product-actions.clipped .product-add-icon,
.product-actions.clipped .product-add-label,
.product-actions:disabled .product-add-icon,
.product-actions:disabled .product-add-label {
  opacity: 0.65;
}

.product-add-icon {
  font-size: $font-size-sm;
}

.product-add-label {
  font-size: $font-size-sm;
}
</style>
